import React from 'react';
import cn from 'clsx';

import Button from '../../../../library/button/button';
import AddProductButton from '../../../../components/add-product-button/add-product-button';
import MinimumPriceMessage from '../../../../components/minimum-price-message/minimum-price-message';
import ProductQuantity from '../../../../components/product-quantity/product-quantity';
import QuantityDisclaimer from '../../../../components/quantity-disclaimer/quantity-disclaimer';
import ProductListCardPrice from '../product-list-card-price/product-list-card-price';
import ProductListCardPromos from '../product-list-card-promos/product-list-card-promos';

import {
  getNormalizedProductType,
  getPrioritizedArticleStatusProduct,
  getShouldShowMinimumPriceMessage,
  isCanonicalProduct
} from '../../../../shared/utils/product/product';

import { ProductData } from '../../../../shared/types/graphql-types';
import { BundleData, ProductItem } from '../../../../shared/types/product';

import styles from './product-list-card-actions.scss';

interface ProductListCardActionsProps {
  defaultQuantity: number;
  hideFoundItLower?: boolean;
  onAddProductClick: (productItem: ProductItem) => void;
  onQuantityChange: (value: number) => void;
  onViewProductClick?: () => void;
  prebuiltPackage?: BundleData;
  product: ProductData;
  productDetailUrl?: string;
  quantity: number;
  totalQuantity: number;
}

function ProductListCardActions(props: ProductListCardActionsProps) {
  const {
    defaultQuantity,
    hideFoundItLower = false,
    onAddProductClick,
    onQuantityChange,
    onViewProductClick,
    prebuiltPackage,
    product,
    productDetailUrl = '',
    quantity,
    totalQuantity
  } = props;

  const { mapDisplayRule } = product || {};
  const articleStatusProduct = getPrioritizedArticleStatusProduct(
    prebuiltPackage?.product1,
    prebuiltPackage?.product2
  );

  let addToCartDisabled;
  if (prebuiltPackage) {
    addToCartDisabled = articleStatusProduct.addToCartDisabled;
  } else {
    addToCartDisabled = product?.addToCartDisabled;
  }

  const isCanonical = isCanonicalProduct(product);
  const productType = getNormalizedProductType(product);

  const showAddToCart = !addToCartDisabled;

  const showMinimumPriceMessage = getShouldShowMinimumPriceMessage(product);

  function handleQuantityChange(e, updatedValue) {
    onQuantityChange(updatedValue);
  }

  if (!isCanonical && !showAddToCart) return null;

  return (
    <div className={cn(styles['container'], 'product-list-card-actions')}>
      <div className={styles['price-info']}>
        <ProductListCardPrice
          hideFoundItLower={hideFoundItLower}
          hidePercentageSaved
          product={product}
          quantity={quantity}
          showStaticQuantity={Boolean(prebuiltPackage)}
          totalQuantity={totalQuantity}
        />

        {showMinimumPriceMessage && (
          <MinimumPriceMessage message={mapDisplayRule?.message} />
        )}

        <ProductListCardPromos
          product={prebuiltPackage ? prebuiltPackage.product1 : product}
        />
      </div>

      {!prebuiltPackage && (
        <QuantityDisclaimer productType={productType} quantity={quantity} />
      )}

      {isCanonical ? (
        <Button
          className={styles['main-cta']}
          href={productDetailUrl}
          isFullWidth
          isPrimary
          onClick={onViewProductClick}
        >
          View Details
        </Button>
      ) : (
        showAddToCart && (
          <div className={styles['add-to-cart']}>
            <ProductQuantity
              defaultValue={defaultQuantity}
              id="quantity"
              onBlur={handleQuantityChange}
              onChange={handleQuantityChange}
              value={quantity}
            />

            <AddProductButton
              className={styles['main-cta']}
              onClick={onAddProductClick}
              productItem={{
                prebuiltPackage,
                product,
                quantity,
                totalQuantity
              }}
            />
          </div>
        )
      )}
    </div>
  );
}

export default ProductListCardActions;
